<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { useCampaignStore } from '@/stores';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import {
  CampaignStateEnum,
  CampaignPatchActionEnum,
} from '@/apis/generated/dialbunny';
import { DATA_QA } from './end-campaign.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';

const log = logger.getChild('EndCampaign');
log.debug('Mounting');
const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  log.error('Missing campaign ID in modal meta');
  void closeModal();
}

const show = ref(false);

onMounted(() => {
  log.debug('Mounted');
  show.value = true;
});

const campaignStore = useCampaignStore();
const watchCampaign = campaignStore.watch(
  () => props.meta?.id ?? '',
  'EndCampaign',
);

const ending = ref(false);

watch(
  () => watchCampaign.value.error,
  (error) => {
    if (error) {
      log.warn(
        `Error fetching campaign. Status: ${error.response.status}, Message: ${error.message}`,
      );
      void closeModal();
      // show toast
    }
  },
);

watch(
  [() => watchCampaign.value.item, () => watchCampaign.value.loading],
  ([campaignToEnd, loading]) => {
    if (!loading && !campaignToEnd) {
      log.log('Campaign is deleted');
      void closeModal();
      // show toast
    }
  },
);

watch(
  () => watchCampaign.value.item?.state,
  (state) => {
    if (
      state !== CampaignStateEnum.Active &&
      state !== CampaignStateEnum.Paused
    ) {
      log.log('Campaign is not active or paused');
      void closeModal();
      // show toast
    }
  },
);

const confirm = async () => {
  if (!watchCampaign.value.item?.id) {
    log.error('campaign ID is missing in watchCampaign');
    return;
  }
  ending.value = true;
  log.debug('Ending campaign');
  campaignStore.update(
    watchCampaign.value.item.id,
    {
      action: CampaignPatchActionEnum.Stop,
    },
    {
      onSuccess: () => {
        // show toast
        log.debug('Campaign ended');
        ending.value = false;
        void closeModal();
      },
      onRetry: () => {
        // show toast
      },
      onError: (error) => {
        log.log('Error ending campaign', error);
        ending.value = false;
        // show toast
      },
    },
  );
};

const cancel = () => {
  void closeModal();
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('END_CAMPAIGN_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('END_CAMPAIGN_DESCRIPTION') }}
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton
        :data-qa="DATA_QA.CANCEL"
        importance="clear"
        kind="muted"
        @click="cancel"
      >
        {{ $t('END_CAMPAIGN_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        :loading="ending"
        class="d-ml8"
        importance="primary"
        kind="danger"
        @click="confirm"
      >
        {{ $t('END_CAMPAIGN_CONFIRM') }}
      </DtButton>
    </div>
  </DtModal>
</template>
